import { Component, Output, EventEmitter, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from "@angular/forms";
import { Router } from "@angular/router";
import { HeaderComponent } from "../components/header/header.component";
import { SidebarComponent } from "../components/sidebar/sidebar.component";
import { SubheaderComponent } from "../components/subheader/subheader.component";
import { DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { CalendarModule } from "primeng/calendar";
import { ButtonModule } from "primeng/button";
import { InputNumberModule } from "primeng/inputnumber";

@Component({
  selector: "app-upload-documents",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    SidebarComponent,
    SubheaderComponent,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    CalendarModule,
    ButtonModule,
    InputNumberModule
  ],
  templateUrl: "./upload-documents.component.html",
  styleUrl: "./upload-documents.component.css",
})
export class UploadDocumentsComponent implements OnInit {
  documentsForm!: FormGroup;

  constructor(
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  @Output() backToWelcome = new EventEmitter<void>();
  @Output() navigateToNext = new EventEmitter<void>();

  // Documento destino seleccionado para el drop/upload del área superior
  selectedTargetKey: 'addressProof' | 'incomeProof' = 'addressProof';

  // Estado de arrastre (para resaltar el área)
  isDragging = false;

  files = [
    {
      key: 'addressProof',
      label: 'Comprobante de domicilio',
      icon: 'pi pi-user',
      progress: 0,
      done: false,
      disabled: false as boolean,
      file: null as File | null
    },
    {
      key: 'incomeProof',
      label: 'Comprobante  de ingresos',
      icon: 'pi pi-users',
      progress: 0,
      done: false,
      disabled: true as boolean,
      file: null as File | null
    }
  ];

  private initializeForm(): void {
    this.documentsForm = this.fb.group({
      addressProof: [null, [this.fileRequiredValidator, this.fileTypeValidator, this.fileSizeValidator]],
      incomeProof: [null, [this.fileRequiredValidator, this.fileTypeValidator, this.fileSizeValidator]] // Ahora requerido
    });
  }

  // Validador personalizado para archivos requeridos
  private fileRequiredValidator(control: AbstractControl): { [key: string]: any } | null {
    const file = control.value;
    if (!file) {
      return { required: true };
    }
    return null;
  }

  // Validador para tipos de archivo permitidos
  private fileTypeValidator(control: AbstractControl): { [key: string]: any } | null {
    const file = control.value as File;
    if (!file) return null;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return { invalidFileType: true };
    }
    return null;
  }

  // Validador para tamaño de archivo (máximo 5MB)
  private fileSizeValidator(control: AbstractControl): { [key: string]: any } | null {
    const file = control.value as File;
    if (!file) return null;
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { fileTooLarge: true };
    }
    return null;
  }

  // Métodos para obtener controles del formulario
  get addressProof() { return this.documentsForm.get('addressProof'); }
  get incomeProof() { return this.documentsForm.get('incomeProof'); }

  // Método para obtener mensajes de error
  getErrorMessage(fieldName: string): string {
    const field = this.documentsForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return fieldName === 'addressProof' ? 'Debe subir el comprobante de domicilio' : 'Debe subir el comprobante de ingresos';
      }
      if (field.errors['invalidFileType']) {
        return 'Tipo de archivo no válido. Use JPG, PNG o PDF';
      }
      if (field.errors['fileTooLarge']) {
        return 'El archivo es demasiado grande. Máximo 5MB';
      }
    }
    return '';
  }

  goBack(): void {
    this.router.navigate(['/verification-module']);
  }

  onSubmit(): void {
    if (this.documentsForm.valid) {
      const formData = this.documentsForm.value;
      console.log('Documentos subidos:', formData);
      this.router.navigate(['/credit-history']);
    } else {
      // Marcar todos los campos como tocados para mostrar errores
      Object.keys(this.documentsForm.controls).forEach(key => {
        this.documentsForm.get(key)?.markAsTouched();
      });
    }
  }

  // Seleccionar la tarjeta como destino
  selectTarget(key: 'addressProof' | 'incomeProof'): void {
    const doc = this.files.find(f => f.key === key);
    if (doc?.disabled) return; // no permitir seleccionar si está deshabilitado
    this.selectedTargetKey = key;
  }

  // Abrir selector de archivos usando el destino actualmente seleccionado
  onUploadClick(fileInput: HTMLInputElement): void {
    fileInput.value = '';
    fileInput.click();
  }

  // Input file: asignar al destino seleccionado
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processSelectedFiles(file);
    input.value = '';
  }

  // Drag & drop
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processSelectedFiles(file);
    }
  }

  // Procesa el archivo para el destino seleccionado
  private processSelectedFiles(file: File): void {
    const target = this.files.find(f => f.key === this.selectedTargetKey);
    if (!target) return;

    // Actualizar el formulario reactivo
    this.documentsForm.patchValue({
      [this.selectedTargetKey]: file
    });

    // Marcar el campo como tocado para activar validaciones
    this.documentsForm.get(this.selectedTargetKey)?.markAsTouched();

    target.file = file;
    target.progress = 100;
    target.done = true;
    target.disabled = false;

    // Si subiste domicilio, habilita ingresos
    if (this.selectedTargetKey === 'addressProof') {
      const income = this.files.find(f => f.key === 'incomeProof');
      if (income) income.disabled = false;
    }
  }

  removeFile(targetKey: 'addressProof' | 'incomeProof', event?: MouseEvent): void {
    event?.stopPropagation(); // evitar que el click seleccione la tarjeta
    const doc = this.files.find(f => f.key === targetKey);
    if (!doc) return;

    // Actualizar el formulario reactivo
    this.documentsForm.patchValue({
      [targetKey]: null
    });

    // Marcar el campo como tocado
    this.documentsForm.get(targetKey)?.markAsTouched();

    doc.file = null;
    doc.progress = 0;
    doc.done = false;

    if (targetKey === 'addressProof') {
      const income = this.files.find(f => f.key === 'incomeProof');
      if (income) {
        income.disabled = true;
        income.file = null;
        income.progress = 0;
        income.done = false;
        
        // También limpiar del formulario
        this.documentsForm.patchValue({ incomeProof: null });
      }
      // Si el seleccionado era ingresos pero ahora queda deshabilitado, vuelve a domicilio
      if (this.selectedTargetKey === 'incomeProof') {
        this.selectedTargetKey = 'addressProof';
      }
    }
  }

  get canContinue(): boolean {
    return this.documentsForm.valid; // Ahora requiere que todo el formulario sea válido
  }

  onContinue(): void {
    this.onSubmit();
  }
}
